<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-8">
        <h2>{{ isEsgContext ? $t('esgPlatform.surveyBuilder') : $t('surveyBuilder.surveyBuilder') }}</h2>
        <v-spacer></v-spacer>
        <div class="d-flex mt-1">
          <v-btn color="primary" dark class="mr-2" width="170" @click="openAssessmentSettings = true">
            {{ $t('esgPlatform.createnewSurvey') }}
          </v-btn>
        </div>
      </div>
      <v-card elevation="0" class="px-5 mt-4">
        <div class="pa-4" v-if="assessments">
          <v-data-table :headers="headers" :items="assessments" :items-per-page="10" :footer-props="{
            'items-per-page-options': [10, 25, 50],
          }" :header-props="headerProps" hide-default-footer :loading="assessmentLoading"
            :options.sync="tableOptions" :server-items-length="totalAssessments" :loading-text="$t('esgPlatform.loadingAssessments')">
            <template v-slot:top>
              <div class="d-flex mt-5">
                <span style="width: 250px" class="shrink mr-4"></span>
                <span style="width: 220px" class="shrink mr-4">{{ $t('esgPlatform.type') }}
                  <v-tooltip top color="00BD73" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon size="16" style="color:#0EBF67" v-bind="attrs" v-on="on">
                        mdi-alert-circle
                        </v-icon>
                    </template>
                    <span>{{ $t('esgPlatform.typeOfTheSurvey') }}</span>
                  </v-tooltip>
                </span>
                <span style="width: 220px" class="shrink mr-4">{{ $t('status') }}
                  <v-tooltip top color="00BD73" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon size="16" style="color:#0EBF67" v-bind="attrs" v-on="on">
                        mdi-alert-circle
                        </v-icon>
                    </template>
                    <span>{{ $t('esgPlatform.activationStatusOfSurvey') }}</span>
                  </v-tooltip>
                </span>
                <span style="width: 220px" class="shrink mr-4">{{ $t('esgPlatform.sort') || 'Sort' }}
                  <v-tooltip top color="00BD73" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon size="16" style="color:#0EBF67" v-bind="attrs" v-on="on">
                        mdi-alert-circle
                        </v-icon>
                    </template>
                    <span>{{ $t('esgPlatform.sortSurveysByDate') }}</span>
                  </v-tooltip>
                </span>
                <span v-if="isClientAdmin" class="shrink mr-4 d-flex align-center" style="width: 160px">
                  {{ $t('esgPlatform.mySurveys') || 'My Surveys' }}
                </span>
              </div>
              <div class="d-flex">

                <v-text-field 
                  id="assessment-name" 
                  prepend-inner-icon="mdi-magnify" 
                  outlined 
                  height="5px"
                  :placeholder="$t('esgPlatform.searchSurveysByTitle')" 
                  dense 
                  class="shrink mr-4" 
                  style="width: 250px"
                  v-model="search" 
                  @input="debouncedSearchAssessment" 
                  clearable
                ></v-text-field>

                <v-select
                  v-model="type"
                  :items="filteredSurveyTypes"
                  item-text="label"
                  item-value="value"
                  outlined
                  dense
                  :clearable="false"
                  :readonly="isEsgContext"
                  style="width: 220px"
                  class="shrink mr-4"
                  :placeholder="$t('esgPlatform.type')"
                  @change="onFilterChange"
                ></v-select>

                <v-select
                  v-model="surveyStatus"
                  :items="surveyStatuses"
                  item-text="label"
                  item-value="value"
                  outlined
                  dense
                  clearable
                  style="width: 220px"
                  class="shrink mr-4"
                  :placeholder="$t('status')"
                  @change="onFilterChange"
                ></v-select>

                <v-select
                  v-model="sort"
                  :items="sortOptions"
                  item-text="label"
                  item-value="value"
                  outlined
                  dense
                  style="width: 220px"
                  class="shrink mr-4"
                  :placeholder="$t('esgPlatform.sort') || 'Sort'"
                  @change="onSortChange"
                ></v-select>

                <v-switch
                  v-if="isClientAdmin"
                  v-model="mySurveysOnly"
                  color="primary"
                  class="shrink mr-4 mt-1"
                  style="width: 160px"
                  hide-details
                  :label="$t('esgPlatform.mySurveys') || 'My Surveys'"
                  @change="onMySurveysToggle"
                ></v-switch>

                <v-spacer></v-spacer>
                <div class="py-1 title">
                  {{ from }} - {{ to }} of {{ totalAssessments }}
                </div>
                <v-btn class="mx-2" fab small outlined color="primary" :disabled="tableOptions.page <= 1"
                  @click="pageChange(false)">
                  <v-icon dark> mdi-chevron-left </v-icon>
                </v-btn>
                <v-btn class="mx-2" fab small outlined color="primary" :disabled="tableOptions.page >= totalPages"
                  @click="pageChange(true)">
                  <v-icon dark> mdi-chevron-right </v-icon>
                </v-btn>
              </div>
            </template>
            <template v-if="assessments.length > 0" v-slot:body="{ items }">
              <tbody>
                <tr v-for="item in items" :key="item.id" style="position: relative; width: 0; height: 0">
                  <td class="text-left text-truncate-title">
                    {{ item.title }}
                  </td>
                  <td class="text-truncate-desc text-left">
                    {{ item.type || "N/A" }}
                  </td>
                  <td class="col-question text-center">{{ item.noOfQuestions }}</td>
                  <!-- <td class="col-response text-center">{{ item.noOfResponse }}</td> -->
                  <td :class="`text-left ${item.isDisabled ? 'red--text' : ''}`">
                    <div class="space-between">
                      <v-chip class="" :color="item.isDisabled ? 'red' : 'success'" outlined>
                        {{ item.isDisabled ? $t('esgPlatform.inactive') : $t('esgPlatform.active') }}
                      </v-chip>
                      <v-menu
                        bottom
                        left
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>

                        <v-list class="py-0">
                          <v-list-item v-for="(action, i) in getDynamicActions(item)" :key="i">
                            <v-list-item-title @click="surveyAction(action, item)">
                              <span :class="'mr-16' + (action === 'Delete' ? ' red--text': '')">{{ getActionDisplayLabel(action) }}</span>
                            </v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
            <template v-slot:no-data>
              <h2>{{ $t('esgPlatform.noSurveysFound') }}</h2>
            </template>
            <template v-slot:footer></template>
          </v-data-table>
        </div>
      </v-card>
    </v-container>
    <CreateSurvey
      v-if="openAssessmentSettings"
      :dialogI="openAssessmentSettings"
      :surveyContext="surveyContext"
      @closeModal="closeModel"
    />

    <confirm-box 
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :cancelBtnText="confirmDelete.cancelBtnText" 
      :confirmBtnText="confirmDelete.confirmBtnText"
      :confirmData="confirmDelete.confirmData" 
      :dialog="confirmDeleteDialog" 
      @cancel:action="confirmDeleteDialog = false"
      @continue:action="confirmDeleteAssessment" />
  </div>
</template>

<script>
import DeforestationService from "@/_services/DeforestationService";
import loadingMixin from "@/mixins/LoadingMixin";
import SurveyOwnershipMixin from "@/mixins/SurveyOwnershipMixin";
import debounce from 'lodash/debounce';
import ConfirmBox from "@/components/ConfirmBox";
import EsgService from "@/_services/EsgService";
import CreateSurvey from './components/CreateSurvey.vue';


export default {
  components: {
    CreateSurvey,
    "confirm-box": ConfirmBox,
  },
  computed: {
    isClientAdmin() {
      return !this.$store.getters.isSuperAdmin;
    },
    // The ESG platform survey list (route `ESGSurveyList`) is scoped to ESG Goal
    // Surveys; every other entry point (e.g. `/survey-builder/Standard-Survey`)
    // is the general "Standard" survey builder.
    surveyContext() {
      return this.$route.name === "ESGSurveyList" ? "esg" : "standard";
    },
    isEsgContext() {
      return this.surveyContext === "esg";
    },
    // Details view route follows the context so the standard builder stays under
    // /survey-builder and the ESG platform stays under /esg-platform.
    detailsRouteName() {
      return this.isEsgContext ? "ESGSurveyDetails" : "SurveyDetails";
    },
    // Breadcrumbs are context-scoped so the standard builder never shows ESG.
    breadcrumbs() {
      if (this.isEsgContext) {
        return [
          {
            text: this.$t("esgPlatform.esgPlatform"),
            disabled: false,
            to: { name: "esgAccountDashboard" },
          },
          {
            text: this.$t("esgPlatform.surveyBuilder"),
            link: true,
            exact: true,
            disabled: true,
          },
        ];
      }
      return [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: { name: "Dashboard" },
        },
        {
          text: this.$t("surveyBuilder.surveyBuilder"),
          link: true,
          exact: true,
          disabled: true,
        },
      ];
    },
    // Type dropdown options per context: ESG list only offers "ESG Goal Survey"
    // (locked), the Standard builder offers the three non-ESG types.
    filteredSurveyTypes() {
      if (this.isEsgContext) {
        return this.surveyTypes.filter((t) => t.value === "ESG Goal Survey");
      }
      return this.surveyTypes.filter((t) => t.value !== "ESG Goal Survey");
    },
  },
  data() {
    return {
      mySurveysOnly: false,
      sort: 'desc',
      sortOptions: [
        { label: this.$t('esgPlatform.newestFirst'), value: 'desc' },
        { label: this.$t('esgPlatform.oldestFirst'), value: 'asc' },
      ],
      selectedItem: {},
      openAssessmentSettings: false,
      openEditAssessmentSetting: false,
      openCreateAssessment: false,
      assessments: [],
      search: "",
      type: "",
      surveyStatus: "",
      headerProps: {
        "sort-icon": "mdi-menu-up",
      },
      assessmentLoading: false,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      totalAssessments: 0,
      countries: [],
      countryOptions: [],
      from: 0,
      to: 0,
      totalPages: 1,
      status: {
        IN_ACTIVE: this.$t('inactive'),
        ACTIVE: this.$t('active'),
      },
      headers: [
        {
          text: this.$t('esgPlatform.surveyTitle'),
          align: "start",
          value: "title",
          class: "black--text",
          width: '30%'
        },
        {
          text: this.$t('esgPlatform.type'),
          align: "start",
          value: "type",
          class: "black--text",
          width: '40%'
        },
        {
          text: this.$t('esgPlatform.question'),
          align: "center",
          value: "noOfQuestions",
          class: "black--text",
          width: 150,

        },
        {
          text: this.$t("status"),
          align: "left",
          value: "isDisabled",
          class: "black--text",
          width: 250,
        },
      ],
      confirmDeleteDialog : false,
      confirmDelete: {
        title: this.$t("confirmDialog.assessmentBuilder.delete.title"),
        message: this.$t("confirmDialog.assessmentBuilder.delete.message"),
        confirmBtnText: this.$t("confirmDialog.assessmentBuilder.delete.confirmBtn"),
        cancelBtnText: this.$t("cancel"),
        confirmData: {},
      },
      surveyTypes: [
        {
          label: this.$t('esgPlatform.standardSurvey'),
          value: "Standard Survey"
        },
        {
          label: this.$t('esgPlatform.riskAssessmentSurvey'),
          value: "Risk Assessment Survey"
        },
        {
          label: this.$t('esgPlatform.esgGoalSurvey'),
          value: "ESG Goal Survey"
        },
        {
          label: this.$t('esgPlatform.rwaSurvey'),
          value: "RWA Survey"
        }
      ],
      surveyStatuses: [
        {
          label: this.$t('esgPlatform.active'),
          value: "active"
        },
        {
          label: this.$t('esgPlatform.inactive'),
          value: "inactive"
        }
      ],
      actions: ['View','Delete',
      // , 'Edit', 'Download', 'Duplicate', 'Delete'
    ],
      copyingId: null,
    };
  },
  created() {
    // Always send a default type so each list stays scoped to its context:
    // ESG list -> "ESG Goal Survey", Standard builder -> "Standard Survey".
    this.type = this.isEsgContext ? "ESG Goal Survey" : "Standard Survey";
  },
  mounted() { },
  watch: {
    tableOptions: {
      async handler() {
        await this.getAssessments()
      },
      deep: true,
    },
  },
  methods: {
    async closeModel() {
      this.openAssessmentSettings = false;
      this.openEditAssessmentSetting = false;
      this.getAssessments();
    },
    getActionDisplayLabel(action) {
      const labels = {
        'View': this.$t('esgPlatform.view'),
        'Delete': this.$t('esgPlatform.delete'),
        'Copy': this.$t('esgPlatform.copy'),
        'View Responses': this.$t('surveyBuilder.viewResponses') || 'View Responses',
      };
      return labels[action] || action;
    },
    getDynamicActions(item) {
      // Read-only rows (created by a superadmin / someone else) can only be
      // viewed or copied — no Edit/Delete. Owned rows keep the existing actions.
      const actions = this.isSurveyReadOnly(item) ? ['View', 'Copy'] : [...this.actions];
      // "View Responses" is only meaningful for the standalone Survey builder.
      if (!this.isEsgContext && this.mySurveysOnly) {
        actions.push('View Responses');
      }
      return actions;
    },
    async getAssessments() {
      this.assessmentLoading = true;
      try {
        let params = {
          page: this.tableOptions.page,
          limit: this.tableOptions.itemsPerPage,
          search: this.search || '',
          sort: this.sort,
          type: this.type || undefined,
          status: this.surveyStatus || undefined,
          mysurveys: this.mySurveysOnly ? 'true' : undefined,
        };

        const res = await EsgService.getAssessments(params);

        if (!res.success) {
          throw Error("Server Error!");
        }

        // Handle the actual response structure from the API
        let assessmentData = [];
        let totalCount = 0;

        if (res.data && res.data.info && Array.isArray(res.data.info)) {
          // Current API response structure with 'info' array
          assessmentData = res.data.info;
          totalCount = res.data.total || 0;
        } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
          // Alternative structure with 'data' array
          assessmentData = res.data.data;
          totalCount = res.data.total || 0;
        } else if (res.data && Array.isArray(res.data)) {
          // Direct array response (fallback)
          assessmentData = res.data;
          totalCount = res.data.length;
        } else {
          // No data or unexpected structure
          assessmentData = [];
          totalCount = 0;
        }

        this.assessments = assessmentData.map((r) => ({
          ...r,
          type: this.surveyTypes.find((i) => i.value === r.type)?.label || r.type || "N/A"
        }));
        this.totalAssessments = totalCount;
        this.totalPages = Math.ceil(this.totalAssessments / this.tableOptions.itemsPerPage) ?? 0;
        this.from = this.totalAssessments > 0 ? (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage + 1 : 0;
        this.to = Math.min(this.tableOptions.page * this.tableOptions.itemsPerPage, this.totalAssessments);
      } catch (err) {
        console.log(err);
        this.assessments = [];
        this.totalAssessments = 0;
        this.totalPages = 0;
        this.from = 0;
        this.to = 0;
        this.$notify({
          text: this.$t('esgPlatform.cannotGetAssessments'),
          type: "error",
        });
      }
      this.assessmentLoading = false;
    },
    async copySurvey(item) {
      if (this.copyingId) return;
      this.copyingId = item._id;
      this.startLoading();
      try {
        const res = await EsgService.duplicateSurvey(item._id, {
          title: `${item.title} (Copy)`,
        });
        if (res && res.success && res.data && res.data._id) {
          this.$notify({
            text: this.$t('esgPlatform.surveyDuplicatedSuccessfully'),
            type: "success",
          });
          if (this.isClientAdmin) {
            this.mySurveysOnly = true;
            this.tableOptions.page = 1;
            await this.getAssessments();
          } else {
            this.$router.push({ name: this.detailsRouteName, params: { id: res.data._id } });
          }
        } else {
          this.$notify({
            text: this.$t('esgPlatform.failedToDuplicateSurvey'),
            type: "error",
          });
        }
      } catch (err) {
        console.error("Error duplicating survey:", err);
        this.$notify({
          text: this.$t('esgPlatform.somethingWentWrongDuplicating'),
          type: "error",
        });
      } finally {
        this.copyingId = null;
        this.stopLoading();
      }
    },
    onSortChange() {
      this.tableOptions.page = 1;
      this.getAssessments();
    },
    onFilterChange() {
      this.tableOptions.page = 1;
      this.getAssessments();
    },
    onMySurveysToggle() {
      this.tableOptions.page = 1;
      this.getAssessments();
    },
    surveyAction(action, survey) {
      const actions = {
        view: (item) => {
          this.$router.push({ name: this.detailsRouteName, params: { id: item._id } });
        },
        view_responses: (item) => {
          this.$router.push({ name: "SurveyResponses", params: { id: item._id } });
        },
        edit: (item) => {
          console.log("Edit", item);
        },
        end_survey: (item) => {
          console.log("End Survey", item);
        },
        publish_survey: (item) => {
          console.log("Publish Survey", item);
        },
        download: (item) => {
          console.log("Download", item);
        },
        duplicate: (item) => {
          this.copySurvey(item);
        },
        copy: (item) => {
          this.copySurvey(item);
        },
        delete: (item) => {
          EsgService.deleteAssessment(item._id).then(res => {
            if (res.success) {
              this.$notify({
                text: this.$t('esgPlatform.assessmentDeletedSuccessfully'),
                type: "success",
              });
              this.getAssessments();
            } else {
              this.$notify({
                text: this.$t('esgPlatform.cannotDeleteAssessment'),
                type: "error",
              });
            }
          }).catch(err => {
            console.error(err);
            this.$notify({
              text: this.$t('esgPlatform.errorDeletingAssessment'),
              type: "error",
            });
          });
          console.log("Delete", item);
        }
      };

      actions[action.toLowerCase().replaceAll(" ", "_")](survey);
    },
    pageChange(t) {
      if (t) {
        this.tableOptions.page = this.tableOptions.page + 1;
      } else {
        this.tableOptions.page = this.tableOptions.page - 1;
      }
    },
    debouncedSearchAssessment: debounce(function () {
      this.tableOptions.page = 1; // Reset to first page when searching
      this.searchAssessment();
    }, 1000),
    searchAssessment() {
      this.getAssessments()
    },
   async selectedAssessment(type, selectedAssessments) {console.log(selectedAssessments)
      if (type==='new'){
        this.openAssessmentSettings = true;
      }
      if(type === 'existing', selectedAssessments?.length){
        this.startLoading()
        const loggedInUser = localStorage.getItem('user')
        const user = JSON.parse(loggedInUser);
        try{
        const res = await DeforestationService.createFromDefaultAssessment({
          orgId: user.user_organization.id,
          userId: user.id,
          defaultAssessmentIds:selectedAssessments
        });
        console.log(res)
      }
      catch(err){
        console.log(err);
      }
        this.stopLoading()
        this.getAssessments()     
      }

    },
    getDetails(item) {
      this.$router.push({
        name: "AssessmentDetails",
        params: { id: item.id },
      });
    },
    async handleEditAssessmentClick(item) {
      this.selectedItem = item
      this.openEditAssessmentSetting = true;
    },
     removeAssessment(id) {
      this.confirmDelete.confirmData = { id };
      this.confirmDeleteDialog = true;
    },
    async confirmDeleteAssessment (action, confirmData) {
      this.startLoading();
      const removeAssessmentResponse = await DeforestationService.removeAssessment(confirmData.id);
      this.stopLoading()
      this.confirmDeleteDialog = false;

      if (removeAssessmentResponse.data?.removeAssessment === true) {
        await this.getAssessments();
      }
      else {
        this.$notify({
          text: this.$t('esgPlatform.cannotDeleteAssessment'),
          type: "error",
        });
      }

    },
    showMenu(item) {
      console.log(item);
    }
  },
  mixins: [loadingMixin, SurveyOwnershipMixin],
};
</script>

<style lang="scss" scoped>
::v-deep .v-data-table-header__icon {
  color: white !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

::v-deep .v-list-item {
  cursor: pointer;
  border-bottom: 1px solid #e4e4e4;

  &:hover {
    background-color: #f2f2f2;
  }
}

.space-between {
  display: flex;
  justify-content: space-between;
}
</style>
